import {useHistoryAssetsByIdQuery, useHistoryAssetsQuery} from '@domain/assets'
import {type AmpelPriceLevels, useElectricityPricesDataQuery} from '@domain/electricity'
import {useScheduleQuery} from '@domain/schedule'
import {type ScheduleItem, type ScheduleOtimizationRange} from '@domain/schedule'
import {AssetSnapshot} from '@hconnect/common'
import {Status} from '@hconnect/common/types/enum.types'
import {
  ChartHeaderDatetimeRows,
  GanttChartRows,
  ChartNowIndicator
} from '@hconnect/ganttchart/src/modules'
import {AddGanttChartItemProps} from '@hconnect/ganttchart/src/types/common'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {Box, Skeleton} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {PLANNING_SCHEDULE_CHART_WIDGET_NAMESPACE} from '../../consts'
import type {DatetimeValue} from '../../types'
import {toPixel, LinearScaleFn, TimeScaleFn, useLinearScale} from '../../utils'
import {usePlanningChartScheduleData, usePlanningChartStartEnd} from '../providers'

import {ElectricityPriceTopBar} from './electricityPriceTopBar/ElectricityPriceTopBar'
import {ChartOptimizedPlanStartEndIndicator} from './indicators/ChartOptimizedPlanStartEndIndicator'

const VISIBLE_CELL_TEXT_BREAKPOINT = 24
export const SCHEDULE_ROW_PADDING = 7

export interface ScheduleItemsRenderParams {
  startOfPlan: Moment
  endOfPlan: Moment
  priceLevels: AmpelPriceLevels | undefined
  prices: DatetimeValue<Moment>[]
  scheduleRowHeight: number
  scheduleRowPadding: number
  xScale: TimeScaleFn
  yScale: LinearScaleFn
}

interface PlanningChartGanttProps {
  startOfPlan: Moment
  endOfPlan: Moment
  optimizationRange?: ScheduleOtimizationRange
  dateLabelHeight: number
  hourlyPriceHeight: number
  cellHeight: number
  cellWidth: number
  hoursDisplayed: number
  numberOfAssets: number | undefined
  xScale: TimeScaleFn
  hoursList: Moment[]
  shouldShowCurrentTime?: boolean
  shouldRoundCurrentTime?: boolean
  shouldShowExactPrices?: boolean
  shouldShowActualPricesIndicator?: boolean
  shouldShowColorCodedElectricityPrices?: boolean
  addScheduleItem?: (
    item: Pick<
      ScheduleItem,
      'start' | 'end' | 'assetOperationModeId' | 'assetId' | 'isTransitionTime'
    >
  ) => void
  isScheduleSubmitting?: boolean
  renderScheduleItems?: (params: ScheduleItemsRenderParams) => React.ReactNode
}
export const _PlanningChartGantt = React.forwardRef<HTMLDivElement, PlanningChartGanttProps>(
  (
    {
      startOfPlan,
      endOfPlan,
      dateLabelHeight,
      hourlyPriceHeight,
      cellHeight,
      cellWidth,
      hoursDisplayed,
      xScale,
      hoursList,
      numberOfAssets,
      optimizationRange,
      shouldShowCurrentTime = false,
      shouldRoundCurrentTime = false,
      shouldShowExactPrices = false,
      shouldShowActualPricesIndicator = false,
      shouldShowColorCodedElectricityPrices = false,
      addScheduleItem,
      isScheduleSubmitting = false,
      renderScheduleItems
    },
    ref
  ) => {
    const {plantId} = useUpmPlantContext()
    const {data: assetsById} = useHistoryAssetsByIdQuery({
      plantId,
      timeFrame: [startOfPlan, endOfPlan]
    })
    const scheduleRowHeight = cellHeight - 2 * SCHEDULE_ROW_PADDING
    const {
      startOfChart,
      endOfChart,
      visibleHoursList,
      hoursRange: [displayedHoursOffset]
    } = usePlanningChartStartEnd()
    const timezoneId = useUpmPlantTimezone()
    const {t} = useTranslation(PLANNING_SCHEDULE_CHART_WIDGET_NAMESPACE)
    const getNowLabel = useCallback((time: string) => t('common.nowWithTime', {time}), [t])

    const {data: assets} = useHistoryAssetsQuery({
      plantId,
      timeFrame: [startOfPlan, endOfPlan],
      sorted: true
    })

    const {data: electricityPriceData} = useElectricityPricesDataQuery({
      plantId,
      timezoneId,
      range: [startOfPlan, endOfPlan]
    })
    const isReadOnly = !renderScheduleItems
    const {scheduleId} = usePlanningChartScheduleData()
    const {isFetching: isScheduleFetching} = useScheduleQuery({
      plantId,
      range: [startOfPlan, endOfPlan],
      scheduleId
    })
    const shouldDisableGanttRows = isScheduleSubmitting || isScheduleFetching || isReadOnly
    const {priceLevels, pricePerHour, mergedPrices: prices} = electricityPriceData ?? {}

    const onAddScheduleItem = ({
      rowId: assetId,
      selectedItemXAxisValue: start
    }: AddGanttChartItemProps<Moment>) => {
      const firstAssetOperationModeId: number | undefined =
        assetsById && assetId !== undefined
          ? assetsById[assetId]?.operationModes.filter(
              (operationMode) => operationMode.status !== Status.Deleted
            )?.[0]?.id
          : undefined
      if (firstAssetOperationModeId !== undefined) {
        addScheduleItem?.({
          start: start.toISOString(),
          end: moment.min(start.clone().add(3, 'hours'), endOfPlan).toISOString(),
          assetOperationModeId: firstAssetOperationModeId,
          assetId,
          isTransitionTime: false
        })
      }
    }

    const ganttYScale = useLinearScale({
      domain: [0, numberOfAssets ?? 0],
      range: [0, cellHeight * (numberOfAssets ?? 0)]
    })

    const chartWidth = hoursDisplayed * cellWidth
    const isTextVisible = cellWidth > VISIBLE_CELL_TEXT_BREAKPOINT

    const shouldShowLoader = numberOfAssets === undefined

    const shouldShowPricesRow = shouldShowExactPrices || shouldShowColorCodedElectricityPrices

    return (
      <Box position="relative" width={chartWidth}>
        <ChartHeaderDatetimeRows
          cellWidth={cellWidth}
          isTextVisible={isTextVisible}
          dateLabelHeight={dateLabelHeight}
          visibleHoursList={visibleHoursList}
        />
        <ChartNowIndicator
          hoursDisplayed={hoursDisplayed}
          cellWidth={cellWidth}
          shouldRoundCurrentTime={shouldRoundCurrentTime}
          startOfChart={startOfChart}
          endOfChart={endOfChart}
          timezoneId={timezoneId}
          getNowLabel={getNowLabel}
        />
        {optimizationRange && (
          <ChartOptimizedPlanStartEndIndicator
            hoursDisplayed={hoursDisplayed}
            cellWidth={cellWidth}
            optimizationRange={optimizationRange}
          />
        )}
        <Box sx={{mt: toPixel(dateLabelHeight * 2)}}>
          {shouldShowPricesRow && (
            <ElectricityPriceTopBar
              hoursList={hoursList}
              shouldShowExactPrices={shouldShowExactPrices}
              shouldShowColorCodedPrices={shouldShowColorCodedElectricityPrices}
              shouldShowActualPricesIndicator={shouldShowActualPricesIndicator}
              hoursDisplayed={hoursDisplayed}
              isTextVisible={isTextVisible}
              hourlyPriceHeight={hourlyPriceHeight}
              pricePerHour={pricePerHour}
              cellWidth={cellWidth}
            />
          )}
        </Box>
        <Box sx={{width: chartWidth, height: 1, overflow: 'hidden', position: 'relative'}}>
          {shouldShowLoader ? (
            <Skeleton
              variant="rectangular"
              sx={{
                height: 1,
                width: hoursList.length * cellWidth
              }}
              {...dataTestId('planning_chart_gantt_skeleton')}
            />
          ) : (
            <GanttChartRows<AssetSnapshot, Moment>
              chartStartBoundary={startOfPlan}
              chartEndBoundary={endOfPlan}
              stepWidth={cellWidth}
              displayCurrentTime={{
                xScale,
                timezoneId,
                shouldRoundCurrentTime,
                shouldShowCurrentTime
              }}
              isReadOnly={shouldDisableGanttRows}
              isAddItemEnabled={!!addScheduleItem}
              xAxis={visibleHoursList}
              canvasOffset={displayedHoursOffset}
              onAddItem={onAddScheduleItem}
              ganttChartRows={assets ?? []}
            />
          )}
          <div
            // passing ref here to enable tranform translateX to move chart horizontally
            ref={ref}
            style={{
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'none'
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: chartWidth,
                height: cellHeight * (numberOfAssets ?? 10)
              }}
            >
              {prices &&
                !shouldShowLoader &&
                renderScheduleItems?.({
                  startOfPlan,
                  endOfPlan,
                  priceLevels,
                  prices,
                  scheduleRowHeight,
                  scheduleRowPadding: SCHEDULE_ROW_PADDING,
                  xScale,
                  yScale: ganttYScale
                })}
            </Box>
          </div>
        </Box>
      </Box>
    )
  }
)
export const PlanningChartGantt = React.memo(_PlanningChartGantt)
PlanningChartGantt.displayName = 'PlanningChartGantt'
